/**
 * @file pwa/form-instance-offline.ts
 * @description 通用表單實例（form_instances）的離線讀寫抽象。
 *
 * 設計目標：
 * - 線上：直接 apiClient.patch，與既有行為等價
 * - 離線：把草稿存進 IndexedDB（per-instance upsert，不堆積），同時把
 *   最新一筆 PATCH 請求 upsert 到 outbox（同 instance 至多一條 pending 條目）
 * - 網絡恢復：pwa/sync.ts 的 startAutoSync 會自動把 outbox 條目發到後端
 *
 * 為什麼要 upsert outbox（而非每次保存都 push 一條）：
 * - 草稿狀態下，使用者可能 30 秒內保存 10 次。outbox 堆積 10 條後，
 *   同步時前 9 條會帶舊 version 全部 409 衝突。
 * - 只保留最新一條 PATCH（覆蓋式），同步時只用最新內存 version，
 *   一次成功；版本衝突由後端 409 統一處理。
 *
 * 對接的後端契約（apps/api/src/routes/form-instances.ts）：
 * - PATCH /api/form-instances/:id  body: { data?, status?, version? }
 * - 後端做樂觀鎖：body.version 與 DB 不符 → 409
 * - 後端對 data 做 schemaJson 二次校驗 → 400
 *
 * 與 outbox 的整合：
 * - entityType: 'form_instance'
 * - entityClientLocalId: 用 String(instanceId) 作為唯一槽位
 *   （注意：表單實例是「線上創建後再離線編輯」的流程，instanceId 來自服務端，
 *    這裡用它當作 entityClientLocalId 只是為了 upsert 索引；
 *    若未來支援「離線創建」，需另設 client UUID。）
 */
import { countPendingOutbox, deleteOutbox, getOfflineDb, type OutboxEntry } from '@/pwa/db';
import { apiClient } from '@/lib/api-client';

/** 表單實例（與 routes/_app/audit-tasks/$taskId.form.$instanceId.tsx 共享） */
export interface FormInstance {
  id: number;
  templateId: number;
  templateVersionId: number;
  ownerType: string | null;
  ownerId: number | null;
  data: Record<string, unknown>;
  status: 'draft' | 'submitted' | 'locked';
  version: number;
  createdAt: string;
  updatedAt: string;
}

/** 表單模板版本（含 schemaJson） */
export interface FormTemplateVersion {
  id: number;
  templateId: number;
  version: number;
  schemaJson: unknown;
}

/**
 * PATCH 請求體型別（與後端 patchSchema 對齊）。
 * data 與 status 至少要有一個，version 用於樂觀鎖。
 */
export interface SaveFormInstancePayload {
  /** 完整的表單數據（{ fieldId: value }），覆蓋式更新 */
  data?: Record<string, unknown>;
  /** draft → 草稿；submitted → 提交；locked 由後端流程控制，前端禁傳 */
  status?: 'draft' | 'submitted';
  /** 樂觀鎖版本號，未提供時後端不檢查（不建議） */
  version?: number;
}

/** saveFormInstanceOffline 結果 */
export interface SaveFormInstanceResult {
  /** 是否走的離線路徑（true 表示已入 outbox，等待同步） */
  offline: boolean;
  /** 線上路徑時返回的最新 instance；離線路徑此值為 null */
  instance: FormInstance | null;
  /** 當前 outbox 中 pending 條目總數（用於前端顯示徽章） */
  pendingCount: number;
}

/** outbox entityType 常量 */
const ENTITY_TYPE_FORM_INSTANCE = 'form_instance';
const OUTBOX_WRITE_TIMEOUT_MS = 1500;

/**
 * 找出 outbox 中針對某個 form instance 的 pending 條目（最多一條，因為 upsert）。
 */
async function findPendingOutboxForInstance(instanceId: number): Promise<OutboxEntry | undefined> {
  const db = await getOfflineDb();
  const all = await db.getAllFromIndex('outbox', 'by-entity', ENTITY_TYPE_FORM_INSTANCE);
  return all.find((e) => e.entityClientLocalId === String(instanceId) && e.status === 'pending');
}

function upsertOutboxPatchNative(
  instanceId: number,
  body: SaveFormInstancePayload,
): Promise<string> {
  return new Promise((resolve, reject) => {
    const request = indexedDB.open('zhcert_offline');
    request.onerror = () => reject(request.error ?? new Error('Failed to open offline DB'));
    request.onsuccess = () => {
      const db = request.result;
      const tx = db.transaction('outbox', 'readwrite');
      const store = tx.objectStore('outbox');
      const index = store.index('by-entity');
      const readRequest = index.getAll(ENTITY_TYPE_FORM_INSTANCE);
      let id = '';

      readRequest.onerror = () => {
        db.close();
        reject(readRequest.error ?? new Error('Failed to read offline outbox'));
      };
      readRequest.onsuccess = () => {
        const sameInstanceRows = readRequest.result.filter(
          (entry) => entry.entityClientLocalId === String(instanceId) && entry.status === 'pending',
        ) as OutboxEntry[];
        const existing = sameInstanceRows[0];
        id = existing?.id ?? crypto.randomUUID();
        for (const duplicate of sameInstanceRows) {
          if (duplicate.id !== id) store.delete(duplicate.id);
        }
        store.put({
          ...(existing ?? {
            method: 'PATCH',
            url: `/api/form-instances/${instanceId}`,
            entityType: ENTITY_TYPE_FORM_INSTANCE,
            entityClientLocalId: String(instanceId),
            createdAt: Date.now(),
          }),
          id,
          body,
          attempts: 0,
          lastError: undefined,
          status: 'pending',
        });
      };
      tx.oncomplete = () => {
        db.close();
        resolve(id);
      };
      tx.onerror = () => {
        db.close();
        reject(tx.error ?? new Error('Failed to write offline outbox'));
      };
    };
  });
}

function countPendingOutboxNative(): Promise<number> {
  return new Promise((resolve, reject) => {
    const request = indexedDB.open('zhcert_offline');
    request.onerror = () => reject(request.error ?? new Error('Failed to open offline DB'));
    request.onsuccess = () => {
      const db = request.result;
      const tx = db.transaction('outbox', 'readonly');
      const countRequest = tx.objectStore('outbox').index('by-status').count('pending');
      countRequest.onsuccess = () => {
        db.close();
        resolve(countRequest.result);
      };
      countRequest.onerror = () => {
        db.close();
        reject(countRequest.error ?? new Error('Failed to count offline outbox'));
      };
    };
  });
}

function countPendingOutboxWithFallback(): Promise<number> {
  return withTimeout(countPendingOutbox(), OUTBOX_WRITE_TIMEOUT_MS).catch(() =>
    countPendingOutboxNative(),
  );
}

function withTimeout<T>(promise: Promise<T>, timeoutMs: number): Promise<T> {
  return Promise.race([
    promise,
    new Promise<T>((_, reject) => {
      window.setTimeout(() => reject(new Error('OUTBOX_IDB_TIMEOUT')), timeoutMs);
    }),
  ]);
}

/**
 * 保存表單實例（線上直 PATCH，離線/失敗入 outbox）。
 *
 * 行為：
 * 1. navigator.onLine === false：跳過網絡，直接 upsert outbox
 * 2. navigator.onLine === true：嘗試 apiClient.patch
 *    - 成功：返回最新 instance
 *    - 失敗（網絡錯誤、5xx）：fallback 入 outbox
 *    - 4xx 業務錯誤（含 409 衝突、400 校驗失敗）：直接拋出，由前端決定如何處理
 *
 * @throws 在線時遇到 4xx 錯誤會拋出 ApiError；其他錯誤一律 fallback 到離線路徑
 */
export async function saveFormInstanceOffline(
  instanceId: number,
  payload: SaveFormInstancePayload,
  opts?: { forceOutbox?: boolean },
): Promise<SaveFormInstanceResult> {
  // 已知离线，或调用方强制要求入 outbox（例如 attachments 含负数 tempId）
  if ((typeof navigator !== 'undefined' && !navigator.onLine) || opts?.forceOutbox) {
    await upsertOutboxPatchNative(instanceId, payload);
    const pendingCount = await countPendingOutboxNative();
    return { offline: true, instance: null, pendingCount };
  }

  try {
    const res = await apiClient.patch<{ instance: FormInstance }>(
      `/api/form-instances/${instanceId}`,
      payload,
    );
    const pendingCount = await countPendingOutboxWithFallback();
    return { offline: false, instance: res.instance, pendingCount };
  } catch (err: unknown) {
    // ApiError 帶 status：4xx 視為業務錯誤直接抛出，由前端做 UX 處理
    const status = (err as { status?: number })?.status;
    if (typeof status === 'number' && status >= 400 && status < 500) {
      console.error('[form-instance-offline] PATCH 4xx, not falling back to outbox', err);
      throw err;
    }
    // 其他錯誤（網絡層、5xx）→ fallback 入 outbox
    console.warn('[form-instance-offline] PATCH failed, queueing to outbox', err);
    await upsertOutboxPatchNative(instanceId, payload);
    const pendingCount = await countPendingOutboxNative();
    return { offline: true, instance: null, pendingCount };
  }
}

// ─────────────────────────────────────────────
// 預熱 / 讀取（搭配 PWA runtimeCaching 使用）
// ─────────────────────────────────────────────

/** 表單模板（GET /api/form-templates/:id 的回應） */
interface FormTemplateResponse {
  template: { id: number; code: string; name: string };
  versions: FormTemplateVersion[];
}

/** 表單實例（GET /api/form-instances/:id 的回應） */
interface FormInstanceResponse {
  instance: FormInstance;
}

/**
 * 進入表單頁時預熱：把 instance + template + 對應 version 都觸碰一遍，
 * 讓 Service Worker 的 runtimeCaching 把回應寫入 cache，
 * 之後離線打開可由 cache 直接回應。
 *
 * 同時把 schemaJson 寫入 cache_forms，作為 SW cache 失效時的二級兜底。
 *
 * 失敗時靜默吞下（預熱本來就是 best-effort）。
 */
export async function prefetchFormInstance(instanceId: number): Promise<void> {
  try {
    const inst = await apiClient.get<FormInstanceResponse>(`/api/form-instances/${instanceId}`);
    const tpl = await apiClient.get<FormTemplateResponse>(
      `/api/form-templates/${inst.instance.templateId}`,
    );
    const version = tpl.versions.find((v) => v.id === inst.instance.templateVersionId);
    if (version) {
      const db = await getOfflineDb();
      // 保留 prefetchTaskFull 已寫入的 docxFileId；本模組只負責 schemaJson 兜底
      const existing = await db.get('cache_forms', version.id);
      await db.put('cache_forms', {
        templateVersionId: version.id,
        schemaJson: version.schemaJson,
        docxFileId: existing?.docxFileId ?? null,
        cachedAt: Date.now(),
      });
    }
  } catch (err) {
    console.warn('[form-instance-offline] prefetch failed (best-effort)', err);
  }
}

/**
 * 從 cache_forms 讀取已快取的 schemaJson，作為 SW cache 失效時的二級兜底。
 * 返回 null 表示沒有快取。
 */
export async function getCachedSchema(templateVersionId: number): Promise<unknown> {
  const db = await getOfflineDb();
  const row = await db.get('cache_forms', templateVersionId);
  return row?.schemaJson ?? null;
}

/**
 * 同步成功後（或 conflict 解決後）清除某 instance 的待同步 outbox 條目。
 * 主要用於前端"丟棄離線草稿"按鈕。
 */
export async function discardPendingDraft(instanceId: number): Promise<void> {
  const existing = await findPendingOutboxForInstance(instanceId);
  if (existing) await deleteOutbox(existing.id);
}

/**
 * 讀取某表單 instance 目前待同步的本地草稿 payload。
 * 用於離線重開頁面時，以 outbox 中的最新草稿覆蓋舊的 cache/API 快照。
 */
export async function getPendingDraftPayload(
  instanceId: number,
): Promise<SaveFormInstancePayload | null> {
  const existing = await findPendingOutboxForInstance(instanceId);
  return (existing?.body as SaveFormInstancePayload | undefined) ?? null;
}
